import React, { useState } from 'react';
import { useLoaderData } from "react-router-dom";
import PropTypes from "prop-types";
import { windowDimensions } from 'helpers/sharedJsHelpers';


import {
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";


const { useWindowDimensions, breakpoints } = windowDimensions;

export default function CheckoutForm({ handleRequest }) {
  const state = useLoaderData();
  const { cart, user, tax } = state;
  const userId = user.data.id;
  const chargeableCart = cart.total() > 0;
  const stripe = useStripe();
  const elements = useElements();
  const [message, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [enable, setEnable] = useState(false);
  const [showZipCode, setShowZipCode] = useState(false);
  const { width } = useWindowDimensions();
  const mobileDevice = width < breakpoints.md;

  const fetchPaymentIntent = async () => {
    try {
      const response = await cart.fetchPaymentIntent(userId, tax.zipcode);
      return { clientSecret: response.clientSecret, complete: response.complete };
    } catch (e) {
      return { error: { message: e.responseJSON.error } };
    }
  };

  const handleError = (error) => {
    setIsLoading(false);
    setErrorMessage(error.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe) return;

    setErrorMessage('');
    setIsLoading(true);

    if (chargeableCart) {
      const { error: submitError } = await elements.submit();
      if (submitError) return handleError(submitError);
    }

    const { clientSecret, complete, error: paymentIntentError } = await fetchPaymentIntent();
    if (paymentIntentError) return handleError(paymentIntentError);
    if (complete) return window.location.replace('/purchase-confirmation');

    const { error: paymentError } = await stripe.confirmPayment({
      elements,
      clientSecret: clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/purchase-confirmation`,
        payment_method_data: {
          billing_details: {
            address: {
              country: 'US',
              postal_code: tax.zipcode
            }
          }
        }
      }
    });

    if (paymentError) {
      setIsLoading(false);
    }
  };

  const options = {
    layout: mobileDevice ? 'accordion' : 'tabs',
    fields: {
      billingDetails: {
        address: {
          country: 'never',
          postalCode: 'never'
        }
      }
    }
  };

  const handleZipCode = (zipCode) => {
    tax.zipcode = zipCode;
    handleRequest({
      type: 'SET_ZIP_CODE',
      payload: { zipCode: zipCode }
    });
  };

  const handleChange = (e) => {
    const paymentType = e.value.type;
    const paymentMethod = e.value.payment_method;
    const savedZipCode = paymentMethod?.billing_details?.address?.postal_code;
    setShowZipCode(!paymentMethod);
    setErrorMessage('');

    if (paymentType !== 'card') {
      setShowZipCode(true);
      handleZipCode('');
      return setEnable(true);
    }

    if (e.complete) return setEnable(true);

    handleZipCode(savedZipCode || '');
    setEnable(false);
  };

  const changeZipCode = (e) => {
    e.preventDefault();
    handleZipCode(e.target.value);
  };

  const invalidZipCode = showZipCode && !tax.zipcode;

  return (
    <form onSubmit={handleSubmit} className="background-white flex">
      { chargeableCart && <PaymentElement onChange={handleChange} options={options} />}
      { chargeableCart && showZipCode && <ZipCode onChange={changeZipCode} zipCode={tax.zipcode} /> }
      { message && <ErrorMessage message={message} /> }
      <div className="button-checkout col-md-12">
        <button
          type="submit"
          id="btn-checkout-payment"
          className="btn btn-orange"
          disabled={(isLoading || !enable || invalidZipCode) && chargeableCart}
        >
          { isLoading ? "Submiting" : 'Place your order' }
        </button>
      </div>
    </form>
  );
}

CheckoutForm.propTypes = {
  handleRequest: PropTypes.func,
};


export function ZipCode({ onChange, zipCode }) {
  return (
    <div className="ZipCode">
      <div>
        <span className="zip-label">ZIP code</span>
        <span className="international-zipcode-bubble">
          <span className="Tooltip hoverable">
            ?
            <div className="Tooltip-content">
              For our international customers, you can proceed with your purchase by entering "00000" as your zip code.
            </div>
          </span>
        </span>
      </div>
      <input
        className="stripe"
        required
        onChange={onChange}
        value={zipCode}
        id="zip_code"
      />
    </div>
  );
}

ZipCode.propTypes = {
  onChange: PropTypes.func,
  zipCode: PropTypes.string
};

export function ErrorMessage({ message }) {
  return (
    <div className="col-md-12">
      <div className="alert alert-danger">{message}</div>
    </div>
  );
}

ErrorMessage.propTypes = {
  message: PropTypes.string
};
